import { useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Redirect } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/auth/useAuth';
import { getAuthRedirectUrl } from '@/lib/auth/deepLinks';
import { Logo } from '@/components/ui/Logo';

type Step = 'form' | 'sent';

export default function AuthPage() {
  const { status } = useAuth();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (status === 'authenticated') return <Redirect to="/" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: getAuthRedirectUrl() },
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setStep('sent');
  }

  async function signInWithGoogle() {
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: getAuthRedirectUrl() },
    });
    if (err) {
      if (err.message.includes('provider is not enabled') || err.message.includes('Unsupported provider')) {
        setError('Google sign-in is not configured yet. Use magic link above, or ask the site admin to enable Google OAuth in the Supabase dashboard.');
      } else {
        setError(err.message);
      }
    }
  }

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="flex min-h-full flex-col items-center justify-center bg-[#faf9f7] px-6 py-16">
          <div className="w-full max-w-sm">
            <div className="mb-8 flex justify-center">
              <Logo size="lg" />
            </div>

            {step === 'form' ? (
              <>
                <h1 className="mb-2 text-center font-serif text-2xl font-bold text-gray-900">
                  Sign in to Open iPub
                </h1>
                <p className="mb-8 text-center text-sm text-gray-500">
                  We'll send you a magic link — no password needed.
                </p>

                <form onSubmit={sendMagicLink} className="flex flex-col gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoFocus
                    className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                  />

                  {error && (
                    <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !email.trim()}
                    className="rounded-lg bg-[#2f3e46] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
                  >
                    {loading ? 'Sending…' : 'Send magic link'}
                  </button>
                </form>

                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs text-gray-400">or</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                <button
                  onClick={signInWithGoogle}
                  className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>

                <p className="mt-6 text-center text-xs text-gray-400">
                  By signing in you agree to our terms. Open iPub is a platform for reading and annotation.
                </p>
              </>
            ) : (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <h2 className="mb-2 font-serif text-xl font-bold text-gray-900">Check your email</h2>
                <p className="mb-1 text-sm text-gray-600">
                  We sent a magic link to
                </p>
                <p className="mb-6 text-sm font-semibold text-gray-900">{email}</p>
                <p className="text-sm text-gray-500">
                  Click the link in the email to sign in. The link expires in 1 hour.
                </p>
                <button
                  onClick={() => setStep('form')}
                  className="mt-6 text-sm text-[#1a56a0] hover:underline"
                >
                  Use a different email
                </button>
              </div>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
